import type { Ref } from 'react'

import { TEDDIES, teddyPhotoPath, type TeddyKey } from '@hazel/shared'

import { fallBackToDrawing } from '../components/teddyPicture.js'

export interface MemoryCardProps {
  /** Which position this is, so it can be named for somebody who cannot see it. */
  readonly position: number
  /** Null while it is face down, which is the entire game. */
  readonly teddy: TeddyKey | null
  readonly matched: boolean
  readonly disabled: boolean
  /** Where the keyboard is pointing, if it is pointing here. */
  readonly cursor?: boolean
  /** So the board can put real focus where the cursor is. */
  readonly ref?: Ref<HTMLButtonElement>
  /**
   * Turns this card over.
   *
   * Handed the button itself, so the board can put focus where the finger
   * went. Safari does not give a button focus on click, and without it the
   * keyboard's ring and the browser's focus end up on different cards —
   * untestable here, since jsdom focuses on click the way Chromium does. See
   * DOL-46.
   */
  readonly onFlip: (element: HTMLButtonElement | null) => void
}

/**
 * One card.
 *
 * Always a button, face up or face down, so every card is reachable by keyboard
 * and announced by a screen reader — "card 4, face down" rather than a
 * mystery. A found card is disabled rather than removed, so the board does not
 * reflow under somebody's finger mid-game.
 *
 * The teddy's name is on the face as well as the photograph. That is not a
 * fallback for a missing photo, though it works as one: two photographs of two
 * rabbits are genuinely hard to tell apart on a phone, and a memory game you
 * lose to your own eyesight is not a memory game.
 */
export function MemoryCard({
  position,
  teddy,
  matched,
  disabled,
  cursor = false,
  ref,
  onFlip,
}: MemoryCardProps) {
  const meta = teddy ? TEDDIES[teddy] : undefined
  const label = meta
    ? `Card ${position + 1}, ${meta.name} the ${meta.species}${matched ? ', found' : ''}`
    : `Card ${position + 1}, face down`

  return (
    <button
      type="button"
      ref={ref}
      // One tab stop for the whole board, not one per card. Twenty stops is
      // the "reachable by tabbing" that this game is meant to avoid, and Tab
      // should land where the cursor already is.
      tabIndex={cursor ? 0 : -1}
      aria-label={label}
      // Face-down cards are the game; face-up ones are the state of it.
      aria-pressed={meta !== undefined}
      disabled={disabled || matched || meta !== undefined}
      onClick={(event) => {
        onFlip(event.currentTarget)
      }}
      style={{ touchAction: 'manipulation' }}
      className={[
        'flex aspect-square items-center justify-center overflow-hidden',
        // On the television a card fills a cell of a board that has already
        // been sized to fit the screen, so the margin here is the whole of the
        // spacing between cards — see the note in `memoryLayout.ts` for why it
        // is not a gap. Off the television nothing here applies and the width
        // still leads.
        '[[data-scale=tv]_&]:m-1.5',
        'rounded-(--radius-control) border-2 p-1 transition-colors',
        // A turned-over card lifts very slightly, and only for somebody who has
        // not asked for less movement. Twenty cards on a phone is a lot of
        // surface to animate, and the colour change alone already says
        // everything the movement does.
        'motion-safe:transition-transform motion-safe:duration-200',
        meta ? 'motion-safe:scale-105' : '',
        matched
          ? 'border-teal bg-surface-sunken opacity-70'
          : meta
            ? 'border-border bg-surface'
            : 'border-border bg-surface-sunken hover:border-ink-muted',
        // The keyboard's cursor. Drawn with a ring rather than a border so it
        // sits outside the card and stays legible from a sofa, and heavy
        // enough to find without looking for it.
        cursor ? 'ring-focus ring-4 ring-offset-2' : '',
        disabled && !matched && !meta ? 'cursor-not-allowed' : '',
      ].join(' ')}
    >
      {meta ? (
        <span className="flex size-full flex-col items-center justify-center gap-1">
          <img
            src={teddyPhotoPath(meta.key)}
            alt=""
            className="min-h-0 flex-1 object-contain"
            // No photograph yet: the drawing stands in, and only if that is
            // missing too does the name carry the card on its own.
            onError={(event) => fallBackToDrawing(event.currentTarget, meta.key)}
          />
          <span
            aria-hidden="true"
            className="font-display text-(length:--text-small) text-center leading-tight"
          >
            {meta.name}
          </span>
        </span>
      ) : (
        <span aria-hidden="true" className="font-display text-ink-muted text-(length:--text-title)">
          ?
        </span>
      )}
    </button>
  )
}
